import { skipToken } from '@reduxjs/toolkit/query'

import { TransferStatus } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'

import { useGetTransfersQuery } from '../../../payments/data/transfersApi'
import { DA_FLEXIBLE_NOTE_NAME } from '../utils/noteConstants'

interface UseFlexibleNoteChargeSubmittedCheckParams {
  noteName: string | undefined
  appointmentId: number | null | undefined
  clientId: string | null
  providerId: string
}

interface UseFlexibleNoteChargeSubmittedCheckResult {
  hasSubmittedCharge: boolean
  submittedChargeId: string | undefined
  isLoading: boolean
}

/**
 * Detects whether a DA Flexible Note's session already has a submitted-but-incomplete charge — i.e. a
 * `pending_note` transfer exists for the appointment (ICE-7135). This happens when closing the session
 * created the charge but the note was never completed (note save failed, or the provider navigated
 * away). On returning to the note we lock the close-session fields so they can't diverge from the
 * already-submitted charge.
 *
 * Querying the transfer (rather than relying on `location.state.chargeId`) makes this the source of
 * truth and survives a hard refresh, mirroring `useReversedChargeCheck`.
 *
 * For non-flexible notes the query is skipped and the hook returns `false`/not-loading.
 */
export const useFlexibleNoteChargeSubmittedCheck = ({
  noteName,
  appointmentId,
  clientId,
  providerId,
}: UseFlexibleNoteChargeSubmittedCheckParams): UseFlexibleNoteChargeSubmittedCheckResult => {
  const isFlexibleNote = noteName === DA_FLEXIBLE_NOTE_NAME

  const { data: pendingNoteTransfers, isLoading } = useGetTransfersQuery(
    isFlexibleNote && clientId && appointmentId
      ? {
          provider_id: providerId,
          patient_id: clientId,
          appointment_id: appointmentId,
          status: TransferStatus.PENDING_NOTE,
          showAlert: false,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  )

  const submittedChargeId = pendingNoteTransfers?.data?.[0]?.id

  return {
    hasSubmittedCharge: Boolean(submittedChargeId),
    submittedChargeId,
    isLoading: isFlexibleNote && isLoading,
  }
}
